import { Injectable, computed, inject } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { SmartSearchModel } from 'mbs-ui-kit';
import { SmartSearchValidationState } from 'mbs-ui-kit/smart-search/models';
import { getSmartSearchTagValues } from 'mbs-ui-kit/smart-search/models/smart-search-model';
import {
  GroupByCompanyParams,
  GroupByCompanyResponse,
  SelectLicenseParams
} from 'mbs-ui-shared@components/components/models/license.model';
import { AdminTrialLicenseInfo, AdministratorLicenseAvailabilityInfo } from 'mbs-ui-shared@models/administrator-license-availability-info';
import { AvailableLicenseType, AvailableLicenses, ComputerLicense, LicenseKind, LicenseTypeSummary } from 'mbs-ui-shared@models/License';
import { LicenseType } from 'mbs-ui-shared@models/LicenseType.enum';
import { LicenseMode } from 'mbs-ui-shared@models/ProviderData';
import { SmartSearchTemplateName } from 'mbs-ui-shared@modules/smart-search-templates/models/smart-search-templates-models';
import { LicensesService } from 'mbs-ui-shared@services/licenses.service';
import { LicensesSignalStore } from 'mbs-ui-shared@store/licenses/licenses-signal.store';
import moment from 'moment';
import { Observable, filter, map, of, switchMap, take } from 'rxjs';
import { AuthFacade } from './auth.facade';

const ComputerLicenseTypeKinds = [LicenseKind.Backup, LicenseKind.Rmm, LicenseKind.Connect];

@Injectable({
  providedIn: 'root'
})
export class LicensesSignalFacade {
  private store = inject(LicensesSignalStore);
  private service = inject(LicensesService);
  private auth = inject(AuthFacade);

  // common - auth
  private currentUser = toSignal(this.auth.currentUser$);
  public licenseMode = computed(() => this.currentUser()?.ProviderInfo?.LicenseMode);
  public paymentSystem = computed(() => this.currentUser()?.ProviderInfo?.PaymentSystem);
  public isNoneLicenseMode = computed(() => this.licenseMode() === LicenseMode.None);
  public isYearlyLicenseMode = computed(() => this.getYearlyLicenseMode(this.licenseMode()));
  public isMonthlyLicenseMode = computed(() => this.getMonthlyLicenseMode(this.licenseMode()));
  public isPostPayment = computed(() => this.currentUser()?.ProviderInfo?.IsPostPayment);
  public prePaymentLicenseModeVersion = computed(() => this.getPrePaymentLicenseModeVersion(this.licenseMode()));

  // common: supported license types for a computer
  public supportedLicenseTypes = this.store.supportedLicenseTypes;
  public supportedLicenseTypesLoading = this.store.supportedLicenseTypesLoading;
  public supportedLicenseTypesLoaded = this.store.supportedLicenseTypesLoaded;
  public supportedLicenseTypesWithDisplayName = computed<AvailableLicenseType[]>(() =>
    (this.store.supportedLicenseTypes() ?? []).map((t) => ({
      ...t,
      displayName: this.allLicenseTypesMap().get(t.id as LicenseType)
    }))
  );

  // common: license types
  public allLicenseTypes = this.store.allLicenseTypes;
  public allLicenseTypesLoading = this.store.allLicenseTypesLoading;
  public allLicenseTypesLoaded = this.store.allLicenseTypesLoaded;
  public allLicenseTypesMap = computed(() => new Map((this.store.allLicenseTypes() ?? []).map((s) => [s.type, s.displayName])));

  public loadAllLicenseTypes(force = false): void {
    this.store.loadAllLicenseTypes(force);
  }

  // common: license types by owner
  public licenseTypesByOwnerPrePostLoading = computed(() =>
    this.isPostPayment() ? this.ppProviderLicensesTypesLoading() : this.licenseTypesByOwnerLoading()
  );
  public licenseTypesByOwnerPrePostLoaded = computed(() =>
    this.isPostPayment() ? this.ppProviderLicensesTypesLoaded() : this.licenseTypesByOwnerLoaded()
  );
  public licenseTypesPrePostByOwner = computed<LicenseType[]>(() =>
    this.isPostPayment() ? this.ppProviderLicensesTypes() : this.licenseTypesByOwner()
  );
  public licenseTypesWithDisplayNamePrePostByOwner = computed<LicenseTypeSummary[]>(() =>
    this.isPostPayment() ? this.ppProviderLicensesTypesWithDisplayName() : this.licenseTypesByOwnerWithDisplayName()
  );

  public licenseTypesWithDisplayNamePrePostByOwnerForComputer = computed<LicenseTypeSummary[]>(() =>
    this.isPostPayment() ? this.ppProviderComputerLicensesTypesWithDisplayName() : this.licenseTypesByOwnerWithDisplayName()
  );

  private currentAdministratorSystemLicenses = computed(() => this.currentUser()?.SystemLicenses ?? []);
  private currentAdministratorTrialLicenses = computed(() => this.currentUser()?.AdminTrialLicences ?? {});

  // use it to check system license availability for a specific license type
  public hasAdministratorLicense = (licenseType: LicenseType) =>
    computed(() => this.currentAdministratorSystemLicenses().includes(licenseType));
  // use it to get trial license info for a specific license type
  public getAdministratorTrialLicense = (licenseType: LicenseType) =>
    computed(() => this.getAdminTrialLicenseInfo(this.currentAdministratorTrialLicenses()[licenseType]));

  public hasPremiumSupportLicense = computed(() => this.hasAdministratorLicense(LicenseType.PremiumSupport)());
  public hasRestoreToCloudLicense = computed(() => this.hasAdministratorLicense(LicenseType.RestoreToCloud)());

  public loadPrePostLicenseTypesByOwner(force = false): void {
    !this.isNoneLicenseMode() && this.isPostPayment() ? this.loadPPProviderLicensesTypes(force) : this.loadLicenseTypesByOwner(force);
  }

  public loadSupportedLicenseTypes(params: { hid: string; kind: number; companyId?: string; userId?: string }): void {
    this.store.loadSupportedLicenseTypes(params);
  }

  public invalidateLicenseTypes(): void {
    this.loadAllLicenseTypes(true);
    this.loadPrePostLicenseTypesByOwner(true);
  }

  // prepayment
  public licenseTypesByOwner = this.store.licenseTypesByOwner;
  public licenseTypesByOwnerLoading = this.store.licenseTypesByOwnerLoading;
  public licenseTypesByOwnerLoaded = this.store.licenseTypesByOwnerLoaded;

  public licenseTypesByOwnerWithDisplayName = computed<LicenseTypeSummary[]>(() =>
    (this.store.licenseTypesByOwner() ?? [])
      .filter((t) => this.allLicenseTypesMap().has(t))
      .map((t) => ({ type: t, displayName: this.allLicenseTypesMap().get(t)! }))
  );

  public loadLicenseTypesByOwner(force = false): void {
    !this.isNoneLicenseMode() && this.store.loadLicenseTypesByOwner(force);
  }

  public prepaymentBilling = this.store.prepaymentBilling;
  public prepaymentBillingLoading = this.store.prepaymentBillingLoading;
  public prepaymentBillingLoaded = this.store.prepaymentBillingLoaded;

  public licenseTypesForHistoryByOwner = this.store.licenseTypesForHistoryByOwner;
  public licenseTypesForHistoryByOwnerLoading = this.store.licenseTypesForHistoryByOwnerLoading;
  public licenseTypesForHistoryByOwnerLoaded = this.store.licenseTypesForHistoryByOwnerLoaded;

  public licenseTypesForHistoryByOwnerWithDisplayName = computed<LicenseTypeSummary[]>(() =>
    (this.store.licenseTypesForHistoryByOwner() ?? [])
      .filter((t) => this.allLicenseTypesMap().has(t))
      .map((t) => ({ type: t, displayName: this.allLicenseTypesMap().get(t)! }))
  );

  public loadPrepaymentBilling(force = false): void {
    !this.isNoneLicenseMode() && this.store.loadPrepaymentBilling(force);
  }

  public loadLicenseTypesForHistoryByOwner(force = false): void {
    !this.isNoneLicenseMode() && this.store.loadLicenseTypesForHistoryByOwner(force);
  }

  public licenseTypesByOwnerPrePostLoaded$ = toObservable(this.licenseTypesByOwnerPrePostLoaded);
  public isLicenseTypesFilterValid(search: SmartSearchModel): Observable<SmartSearchValidationState> {
    return of(getSmartSearchTagValues(SmartSearchTemplateName.LicenseType, search)).pipe(
      switchMap((searchTypes) =>
        searchTypes.length
          ? this.licenseTypesByOwnerPrePostLoaded$.pipe(
              filter(Boolean),
              take(1),
              map(() => this.licenseTypesWithDisplayNamePrePostByOwner()),
              map((types) => types.map((t) => t.displayName.toLowerCase())),
              map((types) => searchTypes.every((s) => types.includes(s.toLowerCase()))),
              map((result) => (result ? SmartSearchValidationState.Valid : SmartSearchValidationState.Invalid))
            )
          : of(SmartSearchValidationState.Unknown)
      )
    );
  }

  // postpayment
  public ppProviderLicensesTypesDto = this.store.ppProviderLicensesTypesDto;
  public ppProviderLicensesTypes = computed(() => (this.store.ppProviderLicensesTypesDto() || []).map((t) => t.id as LicenseType));
  public ppProviderLicensesTypesLoading = this.store.ppProviderLicensesTypesLoading;
  public ppProviderLicensesTypesLoaded = this.store.ppProviderLicensesTypesLoaded;

  public ppProviderLicensesTypesWithDisplayName = computed<LicenseTypeSummary[]>(() =>
    (this.ppProviderLicensesTypes() ?? [])
      .filter((t) => this.allLicenseTypesMap().has(t))
      .map((t) => ({ type: t, displayName: this.allLicenseTypesMap().get(t)! }))
  );

  public ppProviderComputerLicensesTypesWithDisplayName = computed<LicenseTypeSummary[]>(() =>
    (this.ppProviderLicensesTypesDto() ?? [])
      .filter((t) => ComputerLicenseTypeKinds.includes(t.kind))
      .filter((t) => this.allLicenseTypesMap().has(t.id))
      .map((t) => ({ type: t.id, displayName: this.allLicenseTypesMap().get(t.id)! }))
  );

  public loadPPProviderLicensesTypes(force = false): void {
    this.store.loadPPProviderLicensesTypes(force);
  }

  // service
  public getLicensesGroupedByCompanies(params: GroupByCompanyParams): Observable<GroupByCompanyResponse> {
    return this.service.getLicensesGroupedByCompanies(params);
  }

  public getLicensesGroupedByCompaniesForExport(params: GroupByCompanyParams): Observable<any> {
    return this.service.getLicensesGroupedByCompaniesForExport(params);
  }

  public getAvailableLicenses(hid: string, licenseTypeId: LicenseType): Observable<AvailableLicenses> {
    return this.service.getAvailableLicenses(hid, licenseTypeId);
  }

  public assignComputerLicense(params: SelectLicenseParams, skipError = false): Observable<ComputerLicense> {
    return this.service.assignComputerLicense(params, skipError);
  }

  public getSupportedLicenseTypes(hid: string, kind: LicenseKind): Observable<AvailableLicenseType[]> {
    return this.service.getSupportedLicenseTypes(hid, kind);
  }

  // utility
  private getYearlyLicenseMode(licenseMode: LicenseMode) {
    return (
      licenseMode === LicenseMode.Yearly ||
      licenseMode === LicenseMode.CumulativeV2Annual ||
      licenseMode === LicenseMode.CumulativeV3Annual ||
      licenseMode === LicenseMode.CumulativeV4Annual
    );
  }

  private getMonthlyLicenseMode(licenseMode: LicenseMode) {
    return (
      licenseMode === LicenseMode.Monthly ||
      licenseMode === LicenseMode.PostPaymentV1Monthly ||
      licenseMode === LicenseMode.CumulativeV2Monthly ||
      licenseMode === LicenseMode.CumulativeV3Monthly ||
      licenseMode === LicenseMode.CumulativeV4Monthly
    );
  }

  private getPrePaymentLicenseModeVersion(licenseMode: LicenseMode): number {
    const versions = {
      [LicenseMode.CumulativeV4Annual]: 4,
      [LicenseMode.CumulativeV4Monthly]: 4,
      [LicenseMode.CumulativeV3Annual]: 3,
      [LicenseMode.CumulativeV3Monthly]: 3,
      [LicenseMode.CumulativeV2Annual]: 2,
      [LicenseMode.CumulativeV2Monthly]: 2,
      [LicenseMode.Monthly]: 1,
      [LicenseMode.Yearly]: 1
    };

    return versions[licenseMode] ?? 0;
  }

  private getAdminTrialLicenseInfo(license: AdministratorLicenseAvailabilityInfo | undefined): AdminTrialLicenseInfo {
    return {
      allowed: license?.Allowed ?? false,
      expired: !(license?.Allowed && (!license.ExpiredDate || moment(license.ExpiredDate).isAfter(new Date())))
    };
  }
}
